import { REGISTERS, registerFields } from "../registers.js";

/* A register as a sheet.
 *
 * Every field, not the handful the table shows. The screen picks columns for
 * reading on a laptop; a person who asked for the file wants the record, and
 * the notes field they cannot see on screen is usually the reason they asked.
 *
 * Columns come from the same definition the forms are built from, so a field
 * added to a register appears in the export the same day rather than the day
 * somebody remembers to add it here.
 */

/* Housekeeping columns nobody asked for and everybody would have to delete. */
const SKIP = new Set(["id", "contract_id", "deleted_at", "created_at", "updated_at",
                      "added_by", "updated_by"]);

/* Whatever the database returns has to survive being put in a cell. Dates and
   numbers pass through as they are, because the writer turns those into real
   dates and numbers; anything structured is flattened rather than printed as
   [object Object]. */
export function cellValue(v) {
  if (v === null || v === undefined || v === "") return null;
  if (Array.isArray(v)) return v.filter((x) => x !== null && x !== "").join(", ") || null;
  if (typeof v === "object") {
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return v;
}

export function registerSheet(registerKey, contract, rows = []) {
  const cfg = REGISTERS[registerKey];
  if (!cfg) throw new Error(`There is no register called ${registerKey}.`);

  const defined = registerFields(registerKey, contract)
    .filter((f) => f.key && !SKIP.has(f.key));

  const columns = defined.map((f) => ({ key: f.key, label: f.label ?? f.key }));

  /* Anything the database computes rather than the form collecting, such as a
     risk score, is named in the register's column list and would otherwise be
     missing from a file whose whole point is the numbers. */
  const named = new Set(columns.map((c) => c.key));
  for (const key of cfg.columns ?? []) {
    if (named.has(key) || SKIP.has(key)) continue;
    columns.push({ key, label: key === "risk_score" ? "Score" : key });
    named.add(key);
  }

  return {
    title: cfg.label,
    columns,
    rows: rows.map((row) => {
      const out = {};
      for (const c of columns) out[c.key] = cellValue(row[c.key]);
      return out;
    }),
  };
}
